import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { formatFullName } from '@air/utilities';

import { SharePrivateCanvasModal } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModal';
import { SharePrivateCanvasRegenerate } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasRegenerate';
import { EditableSharePrivateLink } from '~/components/SharePrivateContentModal/EditableSharePrivateLink';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';

type EditableCanvasSharePrivateLinkProps = {
  shortIdInfo: ShortUrlResponse;
  canvasId: string;
  trackLocation: AirActionTrackingLocation;
};

export const EditableCanvasSharePrivateLink = memo(
  ({ canvasId, trackLocation, shortIdInfo }: EditableCanvasSharePrivateLinkProps) => {
    const [showRegenerateModal] = useAirModal(SharePrivateCanvasRegenerate);
    const [showShareCanvasModal, hideModal] = useAirModal(SharePrivateCanvasModal);
    const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();
    const { members } = useWorkspaceMembers();
    const createdBy = members?.allMembers.find((member) => member.accountId === shortIdInfo.createdBy);

    const onRegenerateClick = useCallback(() => {
      hideModal();

      const showSharePrivateCanvasModal = () => {
        showShareCanvasModal({
          canvasId,
          trackLocation,
        });
      };

      showRegenerateModal({
        canvasId,
        shortIdInfo,
        trackLocation,
        onRegenerated: () => {
          showSharePrivateCanvasModal();
          showTurnOnShareLinkNotifications({
            onManageClick: hideModal,
          });
        },
        onCancel: showSharePrivateCanvasModal,
      });
    }, [
      canvasId,
      hideModal,
      shortIdInfo,
      showRegenerateModal,
      showShareCanvasModal,
      showTurnOnShareLinkNotifications,
      trackLocation,
    ]);

    return (
      <EditableSharePrivateLink
        createdAt={shortIdInfo.createdAt}
        createdBy={createdBy && formatFullName(createdBy.firstName, createdBy.lastName)}
        objectId={canvasId}
        trackLocation={trackLocation}
        shareId={shortIdInfo.id}
        objectType="canvas"
        onRegenerate={onRegenerateClick}
      />
    );
  },
);

EditableCanvasSharePrivateLink.displayName = 'EditableCanvasSharePrivateLink';
